import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, Copy, ChevronLeft, ChevronRight, Package, FilterX } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';
import InfraestructuraForm from './InfraestructuraForm';

interface CompanyUser { id: string; firstName: string; lastName: string; }
interface CatalogItem { id: number; code: string; name: string; isActive: number; }

interface Equipment {
  id: string;
  descripcio: string;
  marca?: string;
  model?: string;
  ubicacio?: string;
  responsableMantenimentUserId?: string;
  vencimentPlaManteniment?: string;
  foto1?: string;
}

const fmtDate = (v?: string) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ca-ES');
};

export default function Infraestructura() {
  const [items, setItems] = useState<Equipment[]>([]);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [grupFilter, setGrupFilter] = useState('');
  const [subgrupFilter, setSubgrupFilter] = useState('');
  const [ambitFilter, setAmbitFilter] = useState('');
  const [grups, setGrups] = useState<CatalogItem[]>([]);
  const [subgrups, setSubgrups] = useState<CatalogItem[]>([]);
  const [ambits, setAmbits] = useState<CatalogItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('descripcio');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortDir('ASC'); }
  };
  const sortIcon = (col: string) => sortBy === col ? <span className="ml-1">{sortDir === 'ASC' ? '↑' : '↓'}</span> : null;

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit: 2000, sortBy, sortDir };
      if (search) params.search = search;
      if (grupFilter) params.grup = grupFilter;
      if (subgrupFilter) params.subgrup = subgrupFilter;
      if (ambitFilter) params.ambit = ambitFilter;
      const res = await api.get<{ success: boolean; data: Equipment[]; totalPages: number; total: number }>('/taller/infraestructura', params);
      setItems(res.data ?? []);
      setTotalPages(res.totalPages ?? 1);
      setTotal(res.total ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, grupFilter, subgrupFilter, ambitFilter, page, sortBy, sortDir]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users').then((r) => setUsers(r.data ?? [])).catch(() => setUsers([]));
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/grups-equipament').then((r) => setGrups((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/subgrups-equipament').then((r) => setSubgrups((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
    api.get<{ success: boolean; data: CatalogItem[] }>('/catalog/ambits-equipament').then((r) => setAmbits((r.data ?? []).filter((g) => g.isActive))).catch(() => {});
  }, []);

  const userName = (id?: string) => {
    if (!id) return '';
    const u = users.find((x) => x.id === id);
    return u ? `${u.firstName} ${u.lastName}` : '';
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Segur que vols eliminar aquest equip?')) return;
    try {
      await api.delete(`/taller/infraestructura/${id}`);
      fetchItems();
    } catch {
      alert('Error en eliminar');
    }
  };

  const handleDuplicate = async (item: Equipment) => {
    try {
      const res = await api.get<{ success: boolean; data: any }>(`/taller/infraestructura/${item.id}`);
      const original = res.data;
      const payload = { ...original, id: undefined, descripcio: `${original.descripcio} (còpia)` };
      const created = await api.post<{ success: boolean; data: Equipment }>('/taller/infraestructura', payload);
      if (created.success) fetchItems();
    } catch {
      alert('Error en duplicar');
    }
  };

  if (selectedId) {
    return (
      <InfraestructuraForm
        equipmentId={selectedId}
        onBack={() => { setSelectedId(null); fetchItems(); }}
      />
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Button
            variant="outline"
            size="sm"
            title="Netejar tots els filtres"
            onClick={() => { setSearch(''); setGrupFilter(''); setSubgrupFilter(''); setAmbitFilter(''); setPage(1); }}
          >
            <FilterX className="h-4 w-4" />
          </Button>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-8"
              placeholder="Cercar per descripció, marca, model, núm. sèrie..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <ExcelFilterDropdown
            label="Grup"
            options={grups.map((g) => ({ value: g.name, label: g.name }))}
            selected={grupFilter ? grupFilter.split(',') : []}
            onApply={(values) => { setGrupFilter(values.join(',')); setPage(1); }}
          />
          <ExcelFilterDropdown
            label="Subgrup"
            options={subgrups.map((g) => ({ value: g.name, label: g.name }))}
            selected={subgrupFilter ? subgrupFilter.split(',') : []}
            onApply={(values) => { setSubgrupFilter(values.join(',')); setPage(1); }}
          />
          <ExcelFilterDropdown
            label="Àmbit"
            options={ambits.map((g) => ({ value: g.name, label: g.name }))}
            selected={ambitFilter ? ambitFilter.split(',') : []}
            onApply={(values) => { setAmbitFilter(values.join(',')); setPage(1); }}
          />
        </div>
        <Button onClick={() => setSelectedId('new')}>
          <Plus className="w-4 h-4 mr-1" /> Nou Equip
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Carregant...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">No hi ha equips</div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs text-muted-foreground">
            <thead className="bg-slate-100 text-muted-foreground">
              <tr>
                <th className="w-12 px-2 py-2" />
                <th className="px-3 py-2 text-left font-medium">
                  <button onClick={() => toggleSort('descripcio')} className="flex items-center text-xs hover:text-foreground transition-colors">Descripció{sortIcon('descripcio')}</button>
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  <button onClick={() => toggleSort('marca')} className="flex items-center text-xs hover:text-foreground transition-colors">Marca{sortIcon('marca')}</button>
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  <button onClick={() => toggleSort('model')} className="flex items-center text-xs hover:text-foreground transition-colors">Model{sortIcon('model')}</button>
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  <button onClick={() => toggleSort('ubicacio')} className="flex items-center text-xs hover:text-foreground transition-colors">Ubicació{sortIcon('ubicacio')}</button>
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  <button onClick={() => toggleSort('responsableMantenimentUserId')} className="flex items-center text-xs hover:text-foreground transition-colors">Responsable{sortIcon('responsableMantenimentUserId')}</button>
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  <button onClick={() => toggleSort('vencimentPlaManteniment')} className="flex items-center justify-end w-full text-xs hover:text-foreground transition-colors">Proper M.{sortIcon('vencimentPlaManteniment')}</button>
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} className={`border-t ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                  <td className="w-12 px-2 py-2">
                    {item.foto1 && !imgErrors.has(item.id) ? (
                      <img
                        src={item.foto1}
                        alt={item.descripcio}
                        className="h-8 w-8 rounded object-cover border"
                        onError={() => setImgErrors((prev) => new Set(prev).add(item.id))}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded border bg-slate-100 flex items-center justify-center">
                        <Package className="h-4 w-4 text-slate-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => setSelectedId(item.id)}>{item.descripcio}</td>
                  <td className="px-3 py-2">{item.marca}</td>
                  <td className="px-3 py-2">{item.model}</td>
                  <td className="px-3 py-2">{item.ubicacio}</td>
                  <td className="px-3 py-2 truncate">{userName(item.responsableMantenimentUserId)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtDate(item.vencimentPlaManteniment)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setSelectedId(item.id)} className="p-1 rounded hover:bg-gray-200 text-gray-500" title="Editar">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleDuplicate(item)} className="p-1 rounded hover:bg-gray-200 text-gray-500" title="Duplicar">
                        <Copy size={13} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Eliminar">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{total} equips</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 rounded border disabled:opacity-40">
              <ChevronLeft size={14} />
            </button>
            <span>Pàgina {page} de {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 rounded border disabled:opacity-40">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
